import React from "react";
import { useWowPath } from "../../../hooks/path-hooks";
import {
  EXPANSION,
  PATCH,
  getAbbreviatedClass,
  getAbbreviatedSpec,
  getFormattedNames,
  getIcyVeinsLinkName,
  getDataHeroName,
  getHeroNameFromSelectedTree,
} from "../../../lib/wowHelpers";
import NoData from "../../Common/NoData";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import { useQueryString } from "../../../hooks/general-hooks";
import StatPriorities from "../Builds/StatPriorities";
import TalentTreeSection from "../Builds/TalentTreeSection";
import { validParams } from "../../../query_parameters/valid_parameters";
import DataDetails from "../../Common/DataDetails";
import PvPStatSection from "./PvPStatSection";
import PvPTalents from "./PvPTalents";
import TierSetContainer from "../Builds/TierSetContainer";
import { DateTime } from "luxon";
import Layout from "../Layout";
import PvPGearGuide from "../../GearGuide/PvPGearGuide";
import PageSectionCrossLinks from "../../CrossLinks/PageSectionCrossLinks";
import { WoWContext } from "../../App";
import { useHeroQueryParams } from "hooks/wow/heroQueryParams";
import HeroTalentSelector from "../Builds/HeroTalentSelector";
import { SPECS_TO_HERO_TREES } from "lib/classes-and-specs";

// TWWTODO: Add hero to parsing
const PvPTalentsContainer = () => {
  const { classType, specType, encounterType, optionalParams } = useWowPath();
  const { gameMode } = useQueryString();
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);
  const { selectedHero, setSelectedHero } = useHeroQueryParams(displayClassName, displaySpecName);
  const dataHeroName = getDataHeroName(getHeroNameFromSelectedTree(selectedHero, displayClassName, displaySpecName));
  const dataOtherHeroName = getDataHeroName(
    getHeroNameFromSelectedTree(selectedHero === "left" ? "right" : "left", displayClassName, displaySpecName),
  );
  const heroSpecName = getHeroNameFromSelectedTree(selectedHero, displayClassName, displaySpecName);

  const { data: pvpData, loading: isLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/PVP/${gameMode ?? "3v3"}/${dataClassName}.json`,
    {
      ssr: true,
    },
  );

  const specs = validParams.builds.specs[classType];
  const allTrees = {};
  for (let i = 0; i < 4; i++) {
    const { data: specData } = useJSONFetcher(`https://static.bigbrain.gg/wow/tree/v16/${classType}_${specs[i]?.value}.json`, {
      ssr: true,
    });
    allTrees[specs[i]?.value] = specData;
  }

  const classAndSpecTree = allTrees[specType];
  const statPriorities = pvpData?.[dataSpecName]?.[dataHeroName]?.stat_priorities?.stat_priorities?.split("-") ?? [];

  if (isLoading) return null;

  const talentTreeSectionData = {
    classType,
    mainStat: pvpData?.[dataSpecName]?.[dataHeroName]?.talents?.max_rating ?? 0,
    popularity: parseFloat(pvpData?.[dataSpecName]?.[dataHeroName]?.talents?.popularity ?? 0).toFixed(1) + "%",
    selectedTalents: pvpData?.[dataSpecName]?.[dataHeroName]?.talents?.talent?.split("-") ?? [],
    classAndSpecTree,
    specType,
    statLabel: "Max Rating",
    statTooltip: "The highest rated player that used this particular talent build in the selected competitive PvP game mode",
    allTrees,
    encounterType: "PvP",
    selectedHero: selectedHero,
  };

  const buildsData = pvpData?.[dataSpecName]?.[dataHeroName];
  const otherHeroBuildsData = pvpData?.[dataSpecName]?.[dataOtherHeroName];

  const topSectionData = {
    dataClassName: dataClassName,
    dataSpecName: dataSpecName,
    chosenHero: {
      pickrate: buildsData ? buildsData?.stats?.popularity : 0,
      rating: buildsData ? buildsData?.stats?.max_rating : 0,
      parses: buildsData ? buildsData?.stats?.unique_player_count : 0,
      tierLetter: buildsData ? buildsData?.stats?.tier : "",
    },
    otherHero: {
      pickrate: buildsData ? otherHeroBuildsData?.stats?.popularity : 0,
      rating: buildsData ? otherHeroBuildsData?.stats?.max_rating : 0,
      parses: buildsData ? otherHeroBuildsData?.stats?.unique_player_count : 0,
      tierLetter: buildsData ? otherHeroBuildsData?.stats?.tier : "",
    },
    specType: specType,
  };

  const pvpContextValue = {
    allTrees,
    selectedTalents: pvpData?.[dataSpecName]?.[dataHeroName]?.talents?.talent?.split("-") ?? [],
    specType,
    selectedHero,
  };

  const hasData = (pvpData?.[dataSpecName]?.[dataHeroName]?.stats?.played ?? 0) > 0;

  const pageDescription = `Everything for ${displaySpecName} ${displayClassName} PvP. The best ${
    SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][0]
  } and ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][1]} ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(
    displayClassName,
  )} hero talents, from BIS Gear to stat priorities, data-driven by top players. ${EXPANSION} ${PATCH}.`;

  const metaDescription = `The Best ${displaySpecName} ${displayClassName} PvP talents, from gear to stat priority with the new ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][0]} and ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][1]} hero talents, in ${EXPANSION}.`;

  return (
    <Layout
      className="wow-pvp-container"
      classType={classType}
      filterPage="pvp"
      metaDescription={metaDescription}
      metaTitle={`${displaySpecName} ${displayClassName} Hero Talents for PvP - BIS Stats, Talents and Gear`}
      pageDescription={pageDescription}
      pageTitle={`PvP Hero Talents, Stats, and Gear`}
      specType={specType}
    >
      {hasData ? (
        <>
          <WoWContext.Provider value={pvpContextValue}>
            <HeroTalentSelector
              displayClassName={displayClassName}
              displaySpecName={displaySpecName}
              activeHero={selectedHero}
              onClick={setSelectedHero}
              specType={specType}
              pageType="PvP"
              data={topSectionData}
            />
            <DataDetails
              encounterType="PvP"
              numLogsTitle="Top Rated Players Analyzed"
              timestamp={DateTime.fromISO(pvpData?.[dataSpecName]?.[dataHeroName]?.stats?.timestamp ?? "").toRelative()}
              parses={pvpData?.[dataSpecName]?.[dataHeroName]?.stats?.unique_player_count ?? 0}
              ratingRange={`${pvpData?.[dataSpecName]?.[dataHeroName]?.stats?.min_rating ?? 0}-${
                pvpData?.[dataSpecName]?.[dataHeroName]?.stats?.max_rating ?? 0
              }`}
              useDetailedTitle
            />
            <PvPStatSection pvpData={pvpData?.[dataSpecName]?.[dataHeroName]} />
            <StatPriorities
              statPriorities={statPriorities}
              encounterType="PvP"
              dataClassName={dataClassName}
              dataSpecName={dataSpecName}
            />
            <TalentTreeSection {...talentTreeSectionData} />
            <PvPTalents
              displayClassName={displayClassName}
              displaySpecName={displaySpecName}
              pvpTalents={pvpData?.[dataSpecName]?.[dataHeroName]?.pvp_talents ?? []}
            />
            <PvPGearGuide
              builds={pvpData?.[dataSpecName]?.[dataHeroName]?.items}
              combos={pvpData?.[dataSpecName]?.[dataHeroName]?.combos}
              classType={classType}
              specType={specType}
              encounterType="PvP"
            />
            <TierSetContainer className={classType} specName={specType} heroSpecName={heroSpecName} encounterType="PvP" />
            <PageSectionCrossLinks
              classType={classType}
              description={`Our friends at Icy Veins have you covered, with an in-depth guide to ready your ${displaySpecName} ${displayClassName} for PvP.`}
              guideData={[
                {
                  description: `The best in-depth guides covering PvP for ${displaySpecName} ${displayClassName}`,
                  link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                    classType,
                  )}-pvp-guide`,
                  title: "PvP Guide",
                },
              ]}
              specType={specType}
            />
          </WoWContext.Provider>
        </>
      ) : (
        <NoData specType={specType} />
      )}
    </Layout>
  );
};

export default PvPTalentsContainer;
